import React, { useEffect, useMemo, useState } from 'react';
import { Modal, BlockStack, Banner, Card, Checkbox, Select, Text, TextField } from '@shopify/polaris';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { configApi } from '../../../services/api/configApi';

function normalizeAccountType(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function accountLabel(account) {
  return account?.label || [account?.number, account?.name].filter(Boolean).join(' ') || account?.internalId || '';
}

function optionsFor(accounts, currentValue, predicate = () => true) {
  const filtered = (accounts || []).filter(predicate);
  const options = [{ label: 'Select account', value: '' }];
  filtered.forEach((account) => {
    const value = String(account.internalId || '');
    if (!value) return;
    options.push({
      label: accountLabel(account),
      value
    });
  });
  if (currentValue && !options.some((option) => String(option.value) === String(currentValue))) {
    options.push({ label: `Configured account ${currentValue}`, value: String(currentValue) });
  }
  return options;
}

export function PayoutSettingsModal({ open, onClose, onSave, config }) {
  useLockBodyScroll(open);
  const initial = useMemo(() => ({
    payout: {
      amountTolerance: '0.01',
      currencyValidationEnabled: 'true',
      needsReviewRetryMode: 'hold',
      depositMode: 'per_payment',
      consolidatedStartAt: '',
      settlementModeEnabledAt: '',
      settlementTolerance: '0.01',
      unsupportedLineHandling: 'block',
      ...(config?.payout || {})
    }
  }), [config]);

  const [local, setLocal] = useState(initial);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  useEffect(() => {
    if (open) setLocal(initial);
  }, [open, initial]);

  useEffect(() => {
    let cancelled = false;
    async function loadAccounts() {
      if (!open) return;
      setAccountsLoading(true);
      setAccountsError('');
      try {
        const resp = await configApi.getNetSuiteAccounts();
        if (!cancelled) setAccounts(Array.isArray(resp?.accounts) ? resp.accounts : []);
      } catch (error) {
        if (!cancelled) {
          setAccounts([]);
          setAccountsError(error.message || 'Failed to load NetSuite accounts');
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    }
    loadAccounts();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const payout = local.payout || {};
  const update = (field, value) => {
    setLocal((prev) => ({
      ...prev,
      payout: {
        ...(prev.payout || {}),
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    const visiblePayoutSettings = { ...payout };
    delete visiblePayoutSettings.invoiceLookup;
    delete visiblePayoutSettings.invoiceLookupUrl;
    delete visiblePayoutSettings.invoiceLookupScript;
    delete visiblePayoutSettings.invoiceLookupDeploy;
    delete visiblePayoutSettings.invoiceLookupAction;
    delete visiblePayoutSettings.invoiceLookupMethod;

    onSave?.('payouts', {
      payout: {
        ...visiblePayoutSettings,
        amountTolerance: String(payout.amountTolerance || '0.01'),
        currencyValidationEnabled: payout.currencyValidationEnabled === true || String(payout.currencyValidationEnabled) === 'true',
        needsReviewRetryMode: payout.needsReviewRetryMode || 'hold',
        paymentAccount: payout.paymentAccount || '',
        clearingAccount: payout.clearingAccount || '',
        bankDepositScript: payout.bankDepositScript || '',
        bankDepositDeploy: payout.bankDepositDeploy || '',
        depositBankAccountId: payout.depositBankAccountId || '',
        settlementClearingAccountId: payout.settlementClearingAccountId || '',
        feeExpenseAccountId: payout.feeExpenseAccountId || '',
        undepositedFundsAccountId: payout.undepositedFundsAccountId || '',
        depositMode: payout.depositMode || 'per_payment',
        consolidatedStartAt: payout.consolidatedStartAt || '',
        settlementModeEnabledAt: payout.settlementModeEnabledAt || payout.consolidatedStartAt || '',
        settlementTolerance: String(payout.settlementTolerance || '0.01'),
        unsupportedLineHandling: 'clearing_review'
      }
    });
  };

  const bankAccount = (account) => normalizeAccountType(account.type) === 'bank' || normalizeAccountType(account.typeId) === 'bank';
  const expenseAccount = (account) => normalizeAccountType(account.type) === 'expense' || normalizeAccountType(account.typeId) === 'expense';
  const bankOptions = (field) => optionsFor(accounts, payout[field], bankAccount);
  const expenseOptions = (field) => optionsFor(accounts, payout[field], expenseAccount);
  const allAccountOptions = (field) => optionsFor(accounts, payout[field]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payout Settings"
      primaryAction={{ content: 'Save Configuration', onAction: handleSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="loose">
          <Card sectioned>
            <BlockStack gap="loose">
              {accountsError ? (
                <Banner status="critical">{accountsError}</Banner>
              ) : null}
              <Text variant="headingSm" as="h3">Invoice Payment Application</Text>
              <Select
                label="Payment account"
                value={payout.paymentAccount || ''}
                onChange={(v) => update('paymentAccount', v)}
                options={allAccountOptions('paymentAccount')}
                disabled={accountsLoading}
              />
              <Select
                label="Clearing account"
                value={payout.clearingAccount || ''}
                onChange={(v) => update('clearingAccount', v)}
                options={allAccountOptions('clearingAccount')}
                disabled={accountsLoading}
                helpText="Optional. Used when invoice payment application should clear an intermediate payment account."
              />
              <TextField
                label="Amount Variance Tolerance"
                value={String(payout.amountTolerance ?? '0.01')}
                onChange={(v) => update('amountTolerance', v)}
                type="number"
                step={0.01}
              />
              <Checkbox
                label="Validate payout currency against invoice currency"
                checked={payout.currencyValidationEnabled === true || String(payout.currencyValidationEnabled ?? 'true') === 'true'}
                onChange={(checked) => update('currencyValidationEnabled', checked)}
              />
              <TextField
                label="Allowed Invoice Statuses"
                value={payout.allowedInvoiceStatuses || ''}
                onChange={(v) => update('allowedInvoiceStatuses', v)}
                placeholder="Comma-separated, e.g. Open, Pending Payment"
              />
              <Select
                label="Needs Review Retry Mode"
                value={payout.needsReviewRetryMode || 'hold'}
                onChange={(v) => update('needsReviewRetryMode', v)}
                options={[
                  { label: 'Hold until reviewed', value: 'hold' },
                  { label: 'Allow future retry', value: 'retry' }
                ]}
              />
            </BlockStack>
          </Card>
          <Card sectioned>
            <BlockStack gap="loose">
              <Text variant="headingSm" as="h3">NetSuite Bank Deposit Posting</Text>
              <Select
                label="Deposit mode"
                value={payout.depositMode || 'per_payment'}
                onChange={(v) => update('depositMode', v)}
                options={[
                  { label: 'Per payment', value: 'per_payment' },
                  { label: 'Deposit settlement first, then pay invoices', value: 'settlement' }
                ]}
              />
              {payout.depositMode === 'settlement' ? (
                <>
                  <TextField
                    label="Settlement mode activation date (optional)"
                    value={payout.settlementModeEnabledAt || payout.consolidatedStartAt || ''}
                    onChange={(v) => setLocal((prev) => ({
                      ...prev,
                      payout: {
                        ...(prev.payout || {}),
                        settlementModeEnabledAt: v,
                        consolidatedStartAt: v
                      }
                    }))}
                    type="date"
                  />
                  <TextField
                    label="Settlement variance tolerance"
                    value={String(payout.settlementTolerance ?? '0.01')}
                    onChange={(v) => update('settlementTolerance', v)}
                    type="number"
                    step={0.01}
                  />
                </>
              ) : null}
              <Select
                label="Deposit bank account"
                value={payout.depositBankAccountId || ''}
                onChange={(v) => update('depositBankAccountId', v)}
                options={bankOptions('depositBankAccountId')}
                disabled={accountsLoading}
              />
              <Select
                label="Settlement clearing account"
                value={payout.settlementClearingAccountId || ''}
                onChange={(v) => update('settlementClearingAccountId', v)}
                options={allAccountOptions('settlementClearingAccountId')}
                disabled={accountsLoading}
              />
              <Select
                label="Fee expense account"
                value={payout.feeExpenseAccountId || ''}
                onChange={(v) => update('feeExpenseAccountId', v)}
                options={expenseOptions('feeExpenseAccountId')}
                disabled={accountsLoading}
              />
              <Select
                label="Undeposited funds / payments clearing account"
                value={payout.undepositedFundsAccountId || ''}
                onChange={(v) => update('undepositedFundsAccountId', v)}
                options={allAccountOptions('undepositedFundsAccountId')}
                disabled={accountsLoading}
                helpText="Optional. Use when payout matching should clear an intermediate payment account."
              />
            </BlockStack>
          </Card>
          <Card sectioned>
            <BlockStack gap="tight">
              <Text variant="headingSm" as="h3">Settlement Rules</Text>
              <Text as="p">Scope: Wayfair payout lines</Text>
              <Text as="p">NetSuite target: Invoice Payment Application</Text>
              <Text as="p">Mismatch handling: needs review</Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
